import type { Club } from '@form-factory/schema';
import { useQuery } from '@tanstack/react-query';
import type { Select as InformedSelect } from 'informed';
import type { ComponentPropsWithoutRef } from 'react';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { aiCoreApi } from '@/services/aiCoreClient';

import { NativeFormSelect } from '../nativeFormSelect/NativeFormSelect';

export interface ClubSelectProps
  extends Omit<ComponentPropsWithoutRef<typeof InformedSelect>, 'children'> {}

export function ClubSelect({ className, ...restProps }: ClubSelectProps) {
  const { chatId } = useChatConfig();

  const { data: clubs = [] } = useQuery({
    queryKey: ['activeClubs'],
    queryFn: () =>
      aiCoreApi.eFitness.clubs.findAll({
        chatId: chatId,
      }),
  });

  // Group clubs by city
  const normalizedClubs = clubs.map((club: Club) => {
    let cityBase = club.city.toLowerCase();
    if (cityBase.startsWith('brno')) {
      cityBase = 'Brno';
    } else {
      cityBase = club.city.split(' ')[0];
      cityBase = cityBase.charAt(0).toUpperCase() + cityBase.slice(1);
    }

    return { ...club, city: cityBase };
  });

  const groupedClubs = Object.groupBy(
    normalizedClubs,
    club => club.city,
  ) as Record<string, Club[]>;

  const sortedGroupedClubs = Object.entries(groupedClubs)
    .map(([city, cityClubs]) => ({
      city,
      clubs: [...cityClubs].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.city.localeCompare(b.city));

  if (!chatId) {
    throw new Error('chatId is required');
  }

  return (
    <NativeFormSelect className={className} {...restProps}>
      {sortedGroupedClubs.map(({ city, clubs: cityClubs }) => (
        <optgroup key={city} label={city}>
          {cityClubs.map(club => (
            <option key={club.id} value={club.clubId}>
              {club.name}
            </option>
          ))}
        </optgroup>
      ))}
    </NativeFormSelect>
  );
}
